import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { QrCode, KeyRound, Copy, Loader2, CheckCircle, AlertCircle, Info, Unplug } from 'lucide-react';
import { toast } from 'sonner';

const formatTime = (value) => {
  if (!value) return 'Never';
  const d = new Date(value.endsWith('Z') ? value : `${value}Z`);
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

/**
 * Links this campaign to Kaizen QR, which generates badges and sends them by email + WhatsApp.
 * The key is shown exactly once — paste it into the Kaizen QR event's "Kaizen Events link".
 */
const KaizenQrSettings = ({ campaignId }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [enabled, setEnabled] = useState(true);

  const applyLink = (data) => {
    setLink(data);
    setWebhookUrl(data?.webhook_url || '');
    setEnabled(data?.enabled ?? true);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/campaigns/${campaignId}/kaizen-qr`);
        applyLink(res.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to load Kaizen QR link');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [campaignId]);

  const generateKey = async () => {
    if (link?.connected && !window.confirm('Generate a new key? The current key stops working immediately, so Kaizen QR must be updated with the new one.')) {
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/campaigns/${campaignId}/kaizen-qr/key`);
      setNewKey(res.data.key);
      applyLink(res.data.link);
      toast.success('Key generated — copy it now, it will not be shown again');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate key');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/campaigns/${campaignId}/kaizen-qr`, { webhook_url: webhookUrl, enabled });
      applyLink(res.data);
      toast.success('Kaizen QR link saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Kaizen QR? The key is destroyed and new registrations stop reaching Kaizen QR. Badges already sent stay valid.')) {
      return;
    }
    setBusy(true);
    try {
      const res = await api.delete(`/campaigns/${campaignId}/kaizen-qr`);
      setNewKey(null);
      applyLink(res.data);
      toast.success('Kaizen QR disconnected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <QrCode className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-outfit font-semibold text-slate-900">Kaizen QR badges</h3>
              <p className="text-sm text-slate-600">Paid registrations become badges automatically, sent by email and WhatsApp.</p>
            </div>
          </div>
          {link?.connected ? (
            <span className="flex items-center gap-1 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" /> {link.enabled ? 'Active' : 'Paused'}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-sm text-slate-500">
              <AlertCircle className="w-4 h-4" /> Not connected
            </span>
          )}
        </div>

        {newKey && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-900 mb-2">
              Copy this key now and paste it into the Kaizen QR event settings. It will not be shown again.
            </p>
            <div className="flex gap-2">
              <Input readOnly value={newKey} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <Button type="button" variant="outline" onClick={() => copy(newKey)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-slate-500">Campaign ID (paste into Kaizen QR)</p>
            <div className="flex items-center gap-2">
              <p className="font-mono text-xs text-slate-900 break-all">{campaignId}</p>
              <button type="button" onClick={() => copy(campaignId)} className="text-slate-500 hover:text-slate-900" aria-label="Copy campaign ID">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-slate-500">Current key</p>
            <p className="font-mono text-xs text-slate-900">{link?.key_prefix ? `${link.key_prefix}…` : '—'}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-slate-500">Last pulled by Kaizen QR</p>
            <p className="text-slate-900">{formatTime(link?.last_pull_at)}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-slate-500">Last instant push</p>
            <p className="text-slate-900">
              {formatTime(link?.last_push_at)}
              {link?.last_push_error && <span className="text-red-600"> — {link.last_push_error}</span>}
            </p>
          </div>
        </div>

        {link?.connected && (
          <div className="space-y-4 mb-6">
            <div>
              <Label htmlFor="kqr-webhook">Kaizen QR webhook URL (for instant badges)</Label>
              <Input
                id="kqr-webhook"
                placeholder="https://…/api/integrations/kaizen-events/webhook"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">Shown on the Kaizen QR event settings page. Without it, badges still arrive within 5 minutes.</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="kqr-enabled" checked={enabled} onCheckedChange={setEnabled} />
              <Label htmlFor="kqr-enabled">Send registrations to Kaizen QR</Label>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button type="button" onClick={generateKey} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
            {link?.connected ? 'Generate new key' : 'Generate key'}
          </Button>
          {link?.connected && (
            <>
              <Button type="button" variant="outline" onClick={save} disabled={busy}>Save</Button>
              <Button type="button" variant="outline" onClick={disconnect} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50">
                <Unplug className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </>
          )}
        </div>
      </Card>

      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex gap-3 text-sm text-blue-900">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <ul className="list-disc ml-4 space-y-1">
            <li>Only paid registrations are shared. Pending attempts never leave Kaizen Events.</li>
            <li>A full refund blocks that registration's badges at the scanner.</li>
            <li>Generating a new key immediately cuts off the old one.</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default KaizenQrSettings;
